import { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { getCities, getDhlCities } from 'src/utils/postalCodes';
import { isNoPostalCodeCountry } from 'src/data/DHLNoPostalCodeCountries';

// Her tuşta istek atmamak için bekleme süresi
const DEBOUNCE_MS = 300;
const LIMIT = 20;

// DHL araması daha yavaş (~1 sn); daha geç ve en az birkaç harfle aranır.
const DHL_DEBOUNCE_MS = 400;
const DHL_MIN_CHARS = 2;

/**
 * Şehir alanı. Kullanıcı yazdıkça seçili ülkenin şehirlerini servisten getirir
 * ("d" -> Dubai ...). Listede olmayan şehir de elle yazılabilir; veri olmayan
 * ülkelerde alan düz metin gibi çalışır. Dışarıya yalnızca şehir adını verir.
 *
 * Posta kodu olmayan ülkelerde DHL yalnızca şehir adıyla yönlendirdiği için öneriler
 * DHL'in kendi servisinden gelir (addressType: pickup gönderici, delivery alıcı).
 */
export default function CityAutocomplete({
  label = 'Şehir',
  countryCode,
  addressType = 'delivery',
  value,
  onChange,
  error,
  helperText,
  placeholder,
  ...other
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // DHL araması sonuçsuz döndü mü (yardım metni için).
  const [notFound, setNotFound] = useState(false);

  // Geç dönen isteklerin güncel sonucu ezmesini engeller.
  const requestIdRef = useRef(0);

  // Dışarıdan gelen değer değişirse (posta kodundan şehir dolması, form sıfırlama) input'u eşitle.
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const useDhl = isNoPostalCodeCountry(countryCode);
  const search = inputValue.trim();
  const tooShort = useDhl && search.length < DHL_MIN_CHARS;

  useEffect(() => {
    setNotFound(false);
    if (!countryCode || !search || tooShort) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    const timer = setTimeout(() => {
      const load = useDhl
        ? getDhlCities({ type: addressType, countryCode, search })
        : getCities({ countryCode, search, limit: LIMIT });
      load.then((list) => {
        if (requestIdRef.current !== requestId) return;
        setNotFound(useDhl && list.length === 0);
        // Aynı şehir farklı bölgelerde tekrar edebilir; adı bir kez listelenir.
        const seen = new Set();
        setOptions(
          list.filter((item) => {
            const key = String(item.city || '').toLocaleLowerCase('tr');
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
        );
        setLoading(false);
      });
    }, useDhl ? DHL_DEBOUNCE_MS : DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [countryCode, search, tooShort, useDhl, addressType]);

  // Alan serbest metin (freeSolo) olduğu için öneri yokken liste hiç açılmaz;
  // DHL aramasının durumu bu yüzden alanın altında gösterilir. Hata mesajı önceliklidir.
  let hint;
  if (useDhl && search && tooShort) hint = `Aramak için en az ${DHL_MIN_CHARS} harf yazın`;
  else if (notFound && !loading) hint = "DHL'de eşleşen şehir bulunamadı";

  return (
    <Autocomplete
      freeSolo
      fullWidth
      options={options}
      filterOptions={(list) => list}
      loading={loading}
      loadingText="Aranıyor…"
      inputValue={inputValue}
      onInputChange={(_event, next, reason) => {
        setInputValue(next);
        if (reason === 'input' || reason === 'clear') onChange(next);
      }}
      onChange={(_event, option) => {
        if (!option) {
          onChange('');
          return;
        }
        onChange(typeof option === 'string' ? option : option.city);
      }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option?.city || '')}
      isOptionEqualToValue={(option, selected) => option.city === (selected?.city ?? selected)}
      renderOption={(props, option) => (
        <li {...props} key={`${option.city}|${option.region || ''}`}>
          <span style={{ fontWeight: 600, marginRight: 8 }}>{option.city}</span>
          {option.region ? <span style={{ color: '#6B7280' }}>{option.region}</span> : null}
        </li>
      )}
      noOptionsText={countryCode ? 'Eşleşen şehir yok, elle yazabilirsiniz' : 'Önce ülke seçin'}
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          error={error}
          helperText={helperText || hint}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...other}
    />
  );
}